import { useCallback, useEffect, useRef } from 'react';
import {
    useTrackRegionalBookingEventMutation,
    RegionalBookingEventType,
    TrackRegionalBookingEventParams,
} from 'infrastructure/redux/adapters/adminApi';
import { useAppSelector } from 'infrastructure/redux/store/hooks';
import { selectAccessToken } from 'infrastructure/redux/slices/auth.selector';

export interface RegionalBookingTrackingContext {
    locationGroupId?: string;
    originalLocationId?: string;
    originalLocationName?: string;
}

const STORAGE_KEY = 'regional_booking_analytics_queue';

/**
 * Get queued events from localStorage
 */
function getQueuedEvents(): TrackRegionalBookingEventParams[] {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
}

/**
 * Add event to localStorage queue
 */
function queueEvent(event: TrackRegionalBookingEventParams): void {
    try {
        const queue = getQueuedEvents();
        queue.push({
            ...event,
            metadata: {
                ...event.metadata,
                queuedAt: new Date().toISOString(),
            },
        });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    } catch (error) {
        console.error('[Regional Analytics] Failed to queue event:', error);
    }
}

/**
 * Clear the event queue
 */
function clearQueue(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Ignore errors
    }
}

/**
 * Hook for tracking regional booking analytics events.
 * Use this to track user interactions with alternative location suggestions.
 * 
 * For anonymous users, events are queued in localStorage and sent after authentication.
 */
export function useRegionalBookingAnalytics() {
    const [trackEvent] = useTrackRegionalBookingEventMutation();
    const accessToken = useAppSelector(selectAccessToken);
    const isAuthenticated = Boolean(accessToken);

    // Prevent double sending of queued events from React Strict Mode
    const hasProcessedQueueRef = useRef(false);

    // Send queued events when user becomes authenticated
    useEffect(() => {
        if (isAuthenticated && !hasProcessedQueueRef.current) {
            const queuedEvents = getQueuedEvents();
            if (queuedEvents.length > 0) {
                hasProcessedQueueRef.current = true;
                console.log(`[Regional Analytics] Sending ${queuedEvents.length} queued events`);

                // Clear queue immediately to prevent duplicates
                clearQueue();

                // Send all queued events
                Promise.all(queuedEvents.map(event => trackEvent(event)))
                    .then(() => {
                        console.log('[Regional Analytics] Queued events sent successfully');
                    })
                    .catch(error => {
                        console.error('[Regional Analytics] Failed to send queued events:', error);
                    });
            }
        }
    }, [isAuthenticated, trackEvent]);

    /**
     * Track an event - sends immediately if authenticated, queues if not
     */
    const track = useCallback(
        (params: TrackRegionalBookingEventParams) => {
            if (isAuthenticated) {
                trackEvent(params);
            } else {
                console.log('[Regional Analytics] Queuing event for later:', params.eventType);
                queueEvent(params);
            }
        },
        [isAuthenticated, trackEvent]
    );

    /**
     * Track when alternative locations are shown to the user
     */
    const trackAlternativesShown = useCallback(
        (
            context: RegionalBookingTrackingContext,
            alternativesCount: number,
            metadata?: Record<string, unknown>
        ) => {
            track({
                eventType: RegionalBookingEventType.ALTERNATIVES_SHOWN,
                locationGroupId: context.locationGroupId,
                originalLocationId: context.originalLocationId,
                metadata: {
                    ...metadata,
                    alternativesCount,
                    originalLocationName: context.originalLocationName,
                },
            });
        },
        [track]
    );

    /**
     * Track when user clicks on an alternative location
     */
    const trackAlternativeClicked = useCallback(
        (
            context: RegionalBookingTrackingContext,
            alternativeLocationId: string,
            alternativeLocationName?: string,
            metadata?: Record<string, unknown>
        ) => {
            track({
                eventType: RegionalBookingEventType.ALTERNATIVE_CLICKED,
                locationGroupId: context.locationGroupId,
                originalLocationId: context.originalLocationId,
                alternativeLocationId,
                metadata: {
                    ...metadata,
                    originalLocationName: context.originalLocationName,
                    alternativeLocationName,
                },
            });
        },
        [track]
    );

    /**
     * Track when user books at an alternative location
     */
    const trackAlternativeBooked = useCallback(
        (
            context: RegionalBookingTrackingContext,
            alternativeLocationId: string,
            alternativeLocationName?: string,
            metadata?: Record<string, unknown>
        ) => {
            track({
                eventType: RegionalBookingEventType.ALTERNATIVE_BOOKED,
                locationGroupId: context.locationGroupId,
                originalLocationId: context.originalLocationId,
                alternativeLocationId,
                metadata: {
                    ...metadata,
                    originalLocationName: context.originalLocationName,
                    alternativeLocationName,
                },
            });
        },
        [track]
    );

    /**
     * Track when user books at the original location
     */
    const trackOriginalBooked = useCallback(
        (context: RegionalBookingTrackingContext, metadata?: Record<string, unknown>) => {
            track({
                eventType: RegionalBookingEventType.ORIGINAL_BOOKED,
                locationGroupId: context.locationGroupId,
                originalLocationId: context.originalLocationId,
                metadata: {
                    ...metadata,
                    originalLocationName: context.originalLocationName,
                },
            });
        },
        [track]
    );

    /**
     * Track when user rejects all alternatives
     */
    const trackAlternativesRejected = useCallback(
        (context: RegionalBookingTrackingContext, metadata?: Record<string, unknown>) => {
            track({
                eventType: RegionalBookingEventType.ALTERNATIVES_REJECTED,
                locationGroupId: context.locationGroupId,
                originalLocationId: context.originalLocationId,
                metadata: {
                    ...metadata,
                    originalLocationName: context.originalLocationName,
                },
            });
        },
        [track]
    );

    /**
     * Track when user accepts a partial duration at alternative
     */
    const trackPartialDurationAccepted = useCallback(
        (
            context: RegionalBookingTrackingContext,
            alternativeLocationId: string,
            requestedDuration: number,
            acceptedDuration: number,
            metadata?: Record<string, unknown>
        ) => {
            track({
                eventType: RegionalBookingEventType.PARTIAL_DURATION_ACCEPTED,
                locationGroupId: context.locationGroupId,
                originalLocationId: context.originalLocationId,
                alternativeLocationId,
                metadata: {
                    ...metadata,
                    originalLocationName: context.originalLocationName,
                    requestedDuration,
                    acceptedDuration,
                },
            });
        },
        [track]
    );

    return {
        trackAlternativesShown,
        trackAlternativeClicked,
        trackAlternativeBooked,
        trackOriginalBooked,
        trackAlternativesRejected,
        trackPartialDurationAccepted,
    };
}

export { RegionalBookingEventType };
